import { Injectable, inject } from '@angular/core';

import { Client } from 'src/providers/client/client';

import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { AccountManager } from 'src/models/account-manager.model';

// Resolved, display-ready account manager fields. Every field is guaranteed non-empty, so
// templates bind them directly. `photoUrl` is the manager's real photo when the backend has
// one; otherwise a generated initials avatar (real manager, no photo) or TIDY's default
// photo (no manager assigned — e.g. the logged-out shell).
export interface AccountManagerDisplay {
  name: string;
  photoUrl: string;
  email: string;
  phone: string;
}

/**
 * Single source of truth for the account manager shown across the app (onboarding pill,
 * More page, Get Started page, account-manager modal). Reads the assigned manager from the
 * customer's GET /api/v1/customer/me payload, falling back to TIDY's default (Thomaz).
 *
 * Auth-gated fetch: prefers the already-cached /me (warm on load), and only reaches out to
 * the backend when the cache lacks a named account manager AND a session token is present.
 * A cosmetic card must never force an UNauthenticated /me request — on the logged-out shell
 * that returns 401, which the ErrorInterceptor turns into a logout + full reload.
 *
 * /me is cached by several callers with different `includes`, so account_manager is
 * sometimes missing. We remember the last named manager for the current token so a later
 * miss or failed refetch doesn't flip the pill to Thomaz. A successful `account_manager:
 * null` (unassigned) or a token change / forceRefresh clears that memory.
 */
@Injectable({ providedIn: 'root' })
export class AccountManagerService {

  // Name / contact shown until the customer's /me carries an assigned account manager.
  static readonly DEFAULT = {
    name: 'Thomaz',
    photoUrl: 'assets/img/thomaz.png',
    email: 'thomaz@tidy.com',
    phone: '+19895642902',
  };

  // Avatar background palette for the generated initials fallback (deterministic per name).
  private static readonly AVATAR_COLORS = [
    '#2D9CDB', '#27AE60', '#F2994A', '#9B51E0', '#EB5757', '#0FB5AE', '#5B8DEF',
  ];

  private client = inject(Client);
  private storage = inject(TidyStorage);
  private cached: AccountManagerDisplay | null = null;
  private cachedToken: string | null = null;

  // forceRefresh bypasses the cache — use it right after events that change WHO the account
  // manager is (e.g. a demo→real account conversion, where the cached /me is still the old
  // account's and would otherwise show the default until the next natural /me refresh).
  async getDisplay(forceRefresh = false): Promise<AccountManagerDisplay> {
    const token = localStorage.getItem('tidy_token');
    if (forceRefresh || token !== this.cachedToken) {
      this.cached = null;
      this.cachedToken = token;
    }

    try {
      let me = forceRefresh ? null : await this.storage.retrieve('me');
      if (!AccountManagerService.managerName(me?.account_manager) && token) {
        me = await this.client.getMe(['account_manager'], true, false);
        if (me?.account_manager === null) {
          this.cached = null;
          return { ...AccountManagerService.DEFAULT };
        }
      }
      const manager = me?.account_manager;
      if (AccountManagerService.managerName(manager)) {
        this.cached = AccountManagerService.toDisplay(manager);
        return this.cached;
      }
    } catch { /* keep this.cached */ }

    return this.cached ?? { ...AccountManagerService.DEFAULT };
  }

  static managerName(manager: AccountManager | null | undefined): string {
    if (!manager) { return ''; }
    return manager.name?.trim()
      || [manager.first_name, manager.last_name].filter(Boolean).join(' ').trim();
  }

  // Merge an AccountManager over the default. A real manager with no photo_url gets a
  // generated initials avatar of THAT name rather than Thomaz's face. Nameless stubs are
  // treated as unassigned — never mix Thomaz's name onto someone else's record.
  static toDisplay(manager: AccountManager | null | undefined): AccountManagerDisplay {
    const d = AccountManagerService.DEFAULT;
    const name = AccountManagerService.managerName(manager);
    if (!manager || !name) {
      return { ...d };
    }
    const photoUrl = manager.photo_url?.trim() || AccountManagerService.initialsAvatar(name);
    return {
      name,
      photoUrl,
      email: manager.email?.trim() || d.email,
      phone: manager.phone?.trim() || d.phone,
    };
  }

  // A round, colored initials avatar as an inline SVG data-URI, so it drops into any existing
  // <img [src]> slot (inheriting that slot's size) with no template changes.
  static initialsAvatar(name: string): string {
    const initials = AccountManagerService.initials(name);
    const bg = AccountManagerService.colorForName(name);
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">` +
      `<rect width="100" height="100" rx="50" fill="${bg}"/>` +
      `<text x="50" y="52" font-family="Arial, Helvetica, sans-serif" font-size="42" ` +
      `font-weight="600" fill="#ffffff" text-anchor="middle" dominant-baseline="central">${initials}</text>` +
      `</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }

  private static initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) { return ''; }
    const first = parts[0][0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
    return (first + last).toUpperCase();
  }

  private static colorForName(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
    }
    const colors = AccountManagerService.AVATAR_COLORS;
    return colors[hash % colors.length];
  }
}
